// Lion Auto floors: a % of each ranked listing's own cost no rank is priced
// under. A group's own floor (criteria.ladderFloorPct) wins when > 0; 0 or
// blank uses the show floor, else the artist floor (the Skybox performer,
// every show of it), else none. The server resolves the floor it prices
// with; these helpers say on screen which one a group uses and word the
// Floors box (LaFloorsPanel): its confirms, the save dialog
// (LaFloorApplyModal), results and errors. Pure; tested in
// lionAutoFloors.test.js.
import { MAX_FLOOR_PCT, cleanFloorPct, pctText } from './lionAutoLadder';
import { agoText, isDryRun, isEnabled } from './lionAutoRunner';
import { detailLines, retryAfterSec } from './lionAutoErrors';

// The sources an inherited floor can come from (GET floors: inherited.source)
const INHERITED_SOURCES = ['show', 'artist'];
// FLOOR_IN_USE names at most this many groups, then a count
const MAX_NAMED_GROUPS = 5;

/** A count from the server (groups): a positive whole number, else 0 */
export const count = (v) => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : 0;
};
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const nameOf = (performerName) => (typeof performerName === 'string' ? performerName.trim() : '');

/**
 * The floor a group prices with: its own floor if > 0, else the inherited
 * (show, else artist) floor if > 0, else none (pct 0). Junk, negative and
 * over-500 values count as none, as on the server.
 * @param {*} ownPct  the group's criteria.ladderFloorPct
 * @param {{ pct, source }|null} inherited  the show's inherited floor (inheritedFloorOf)
 * @returns {{ pct: number, source: 'group'|'show'|'artist'|null }}
 */
export function effectiveFloor(ownPct, inherited) {
  const own = cleanFloorPct(ownPct);
  if (own > 0) return { pct: own, source: 'group' };
  const pct = cleanFloorPct(inherited?.pct);
  const source = inherited?.source;
  if (pct > 0 && INHERITED_SOURCES.includes(source)) return { pct, source };
  return { pct: 0, source: null };
}

/**
 * The show's inherited floor from a GET floors answer, with its performer
 * (for the words): { pct, source, performerName }, or null while unknown.
 */
export function inheritedFloorOf(data) {
  const inherited = data?.inherited;
  if (!inherited || typeof inherited !== 'object') return null;
  return { pct: inherited.pct ?? 0, source: inherited.source ?? null, performerName: data.performerName ?? null };
}

// Known: no inherited floor (a server without the floors route)
const NO_INHERITED_FLOOR = Object.freeze({ pct: 0, source: null, performerName: null });

/**
 * The inherited floor from the floors query ({ data, error }): the answer
 * once read (one without an inherited floor reads as none); none on a 404
 * (an older server without the floors route); null = unknown (loading, or
 * the read failed), which is not "no floor".
 */
export function inheritedFloorFor(query) {
  if (query?.data) return inheritedFloorOf(query.data) || NO_INHERITED_FLOOR;
  if (query?.error?.response?.status === 404) return NO_INHERITED_FLOOR;
  return null;
}

/** Why the ladder waits for the floors (it must know the floor it prices at), or null */
export function floorsBlockedReason(query) {
  if (inheritedFloorFor(query) !== null) return null;
  return query?.error ? 'Could not read the floors: Retry in the Floors box' : 'Reading the floors…';
}

/** Where a floor comes from, short: "own" / "this show" / "Jonas Brothers"; '' for none */
export function floorSourceText(source, performerName) {
  if (source === 'group') return 'own';
  if (source === 'show') return 'this show';
  if (source === 'artist') return nameOf(performerName) || 'artist';
  return '';
}

/** The floor a source names, in a sentence: "this show's floor" / "the Jonas Brothers floor" */
export function floorUseText(source, performerName) {
  if (source === 'group') return 'its own floor';
  if (source === 'show') return "this show's floor";
  if (source === 'artist') return `the ${nameOf(performerName) || 'artist'} floor`;
  return '';
}

/**
 * A group card's floor line: { text: "Floor 130% · Jonas Brothers", title },
 * or null when the group has no floor at all.
 * @param {*} ownPct  the group's criteria.ladderFloorPct
 * @param {{ pct, source, performerName }|null} inherited  inheritedFloorOf
 */
export function floorLabel(ownPct, inherited) {
  const { pct, source } = effectiveFloor(ownPct, inherited);
  if (!(pct > 0)) return null;
  const name = nameOf(inherited?.performerName);
  let title = "This group's own floor";
  if (source === 'show') title = "This show's floor: this group has no floor of its own";
  if (source === 'artist') title = `The ${name || 'artist'} floor (all its shows): this group has no floor of its own`;
  return { text: `Floor ${pctText(pct)} · ${floorSourceText(source, name)}`, title };
}

/**
 * A Floors box's text → the % to save: 0 for blank or 0 (clears), 0.01..500
 * rounded to the cent, or null for anything else (never a partial value).
 */
export function parseFloorText(text) {
  if (text === null || text === undefined) return 0;
  if (typeof text !== 'string' && typeof text !== 'number') return null;
  const s = String(text).trim();
  if (s === '') return 0;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0 || n > MAX_FLOOR_PCT) return null;
  const pct = Math.round(n * 100) / 100;
  // 0.001 is not a floor the server takes, and not a clear either
  return pct === 0 && n !== 0 ? null : pct;
}

/** A saved floor as the box's text: "130", blank for none */
export function pctInputText(pct) {
  const n = Number(pct);
  return pct !== null && pct !== undefined && pct !== '' && Number.isFinite(n) && n > 0 ? String(n) : '';
}

/** "set by Albert · 2h ago" for a saved floor, "not set" for none */
export function floorStampText(entry) {
  if (!entry) return 'not set';
  const by = entry.updatedByName ? `set by ${entry.updatedByName}` : null;
  const when = entry.updatedAt ? agoText(entry.updatedAt) : null;
  if (by && when) return `${by} · ${when}`;
  return by || (when ? `set ${when}` : 'set');
}

/** This show's groups (GET floors: groups.here) in one line */
export function floorCountsText(here) {
  const follow = count(here?.follow);
  const own = count(here?.own);
  const auto = count(here?.autoFollowing);
  const total = follow + own;
  if (!total) return 'No groups on this show yet';
  const parts = [
    `${follow} follow${follow === 1 ? 's' : ''}`,
    `${own} with ${own === 1 ? 'its' : 'their'} own floor`,
  ];
  if (auto) parts.push(`${auto} on Auto follow${auto === 1 ? 's' : ''}`);
  return `This show's ${plural(total, 'group')}: ${parts.join(' · ')}`;
}

const scopeFloorText = (scope, performerName) => (scope === 'artist'
  ? `the ${nameOf(performerName) || 'artist'} floor`
  : "this show's floor");

/**
 * The confirm before a floor change, or null when no Auto group follows that
 * floor (nothing changes unattended).
 * @param {{ scope: 'artist'|'show', pct: number, performerName?: string|null, autoFollowing?: number }} change
 */
export function floorChangeConfirm({ scope, pct, performerName, autoFollowing }) {
  const nextRun = floorNextRunText(autoFollowing);
  if (!nextRun) return null;
  const which = `${scopeFloorText(scope, performerName)}${scope === 'artist' ? ' (all its shows)' : ''}`;
  const ask = pct > 0 ? `Set ${which} to ${pctText(pct)}?` : `Clear ${which}?`;
  return `${ask} ${nextRun}`;
}

/**
 * "3 Auto groups will use the new floor on their next run.", or null for
 * none: the change confirm's warning, and the save dialog's when it offers
 * no "Update prices now" (upTo: its count is an upper bound, "Up to 3 …")
 */
export function floorNextRunText(n, { upTo = false } = {}) {
  const auto = count(n);
  if (!auto) return null;
  const groups = `${plural(auto, 'Auto group')} will use the new floor on ${auto === 1 ? 'its' : 'their'} next run.`;
  return upTo ? `Up to ${groups}` : groups;
}

const AUTO_FOLLOW_TAIL = 'Groups on Auto use it from their next run; a group that would be left with no floor is skipped.';

/**
 * The confirm before "make groups follow" (a bulk edit, so always asked).
 * show scope: inherited = this show's inherited floor; artist scope:
 * performerName and artistPct, the artist's floor.
 */
export function followConfirm({ scope, count: n, inherited = null, performerName = null, artistPct = 0 }) {
  const groups = count(n);
  const one = groups === 1;
  if (scope === 'artist') {
    const name = nameOf(performerName) || 'artist';
    return `Clear the own floor of all ${groups} ${name} group${one ? '' : 's'} that ${one ? 'has' : 'have'} one, on every ${name} show? `
      + `${one ? 'It' : 'Each'} will use its show's floor, else the ${name} floor (${pctText(artistPct)}); show floors stay as they are. `
      + AUTO_FOLLOW_TAIL;
  }
  const floor = effectiveFloor(0, inherited);
  return `Clear the own floor of this show's ${plural(groups, 'group')} that ${one ? 'has' : 'have'} one? `
    + `${one ? 'It' : 'They'} will use ${floorUseText(floor.source, inherited?.performerName)} (${pctText(floor.pct)}). `
    + AUTO_FOLLOW_TAIL;
}

/** What a floor save did */
export function floorSavedText(scope, pct, performerName) {
  const floor = scopeFloorText(scope, performerName);
  return pct > 0 ? `Saved: ${floor} is ${pctText(pct)}.` : `Cleared ${floor}.`;
}

// A count from the server, or a list of ids (applied.groups, reprice.queued)
const sizeOf = (v) => (Array.isArray(v) ? v.length : count(v));

/**
 * The save dialog for a floor above 0, or null when there is nothing to
 * choose (save as today, with floorChangeConfirm). The exceptions are what
 * keeps its own floor today: this show's groups with one (show scope), or
 * the artist's groups with one plus its upcoming shows with a show floor
 * (artist scope). auto: the Auto groups whose floor moves with "Only groups
 * that follow" (follow) and with "Everything" (all). Null with no exception
 * unless the user may re-price (canReprice) and an Auto group would move.
 * A count an older server does not send is 0.
 * @param {{ scope: 'artist'|'show', pct: *, floors: object|null, canReprice?: boolean }} input
 *   floors: the GET floors body
 * @returns {null|{ scope: 'artist'|'show', pct: number, groups: number, shows: number,
 *   exceptions: number, auto: { follow: number, all: number }, canReprice: boolean }}
 */
export function floorApplyChoices({ scope, pct, floors, canReprice = false }) {
  const floor = cleanFloorPct(pct);
  if (!(floor > 0)) return null;
  const artist = scope === 'artist';
  const counts = artist ? floors?.groups?.artist : floors?.groups?.here;
  const groups = count(counts?.own);
  const shows = artist ? count(counts?.showFloors) : 0;
  const auto = { follow: count(counts?.autoFollowing), all: count(counts?.auto) };
  const exceptions = groups + shows;
  const reprice = !!canReprice && (auto.follow > 0 || auto.all > 0);
  if (!exceptions && !reprice) return null;
  return { scope: artist ? 'artist' : 'show', pct: floor, groups, shows, exceptions, auto, canReprice: !!canReprice };
}

/**
 * The save dialog's words for floorApplyChoices: { title, followHint,
 * allLabel, allHint, noExceptions }. With no exception there are no radios:
 * followHint, allLabel and allHint are null and noExceptions says why.
 */
export function floorApplyTexts(choices, performerName) {
  const artist = choices?.scope === 'artist';
  const name = nameOf(performerName);
  const title = `Set ${scopeFloorText(choices?.scope, name)} to ${pctText(cleanFloorPct(choices?.pct))}`;
  const groups = count(choices?.groups);
  const shows = artist ? count(choices?.shows) : 0;
  if (!groups && !shows) {
    const noExceptions = artist
      ? `No ${name || 'artist'} group or show has a floor of its own: they all follow this one.`
      : 'No group on this show has a floor of its own: they all follow this one.';
    return { title, followHint: null, allLabel: null, allHint: null, noExceptions };
  }
  const kept = [];
  const cleared = [];
  if (groups) {
    kept.push(`${plural(groups, 'group')} with ${groups === 1 ? 'its' : 'their'} own floor`);
    cleared.push(plural(groups, 'group floor'));
  }
  if (shows) {
    kept.push(plural(shows, 'show floor'));
    cleared.push(plural(shows, 'show floor'));
  }
  let keepVerb = 'keep theirs';
  if (kept.length === 1 && groups === 1) keepVerb = 'keeps it';
  else if (kept.length === 1 && shows === 1) keepVerb = 'stays';
  let allLabel = 'Every group on this show';
  if (artist) allLabel = name ? `Everything on every upcoming ${name} show` : 'Everything on every upcoming show of the artist';
  return {
    title,
    followHint: `${kept.join(' and ')} ${keepVerb}`,
    allLabel,
    allHint: `clears ${cleared.join(' and ')}`,
    noExceptions: null,
  };
}

/** The Auto groups that re-price with the dialog's choice (applyToAll = "Everything") */
export function floorRepriceCount(choices, applyToAll) {
  return count(applyToAll ? choices?.auto?.all : choices?.auto?.follow);
}

const NO_REPRICE_TEXT = "No Auto group's floor changes with this choice";
// The runner can't run for want of a working Skybox login (GET /auto/status `status`)
const NO_LOGIN_STATUSES = ['paused_auth', 'no_session'];
const NO_LOGIN_TEXT = 'Lion Auto has no working Skybox login: nothing runs until one is fixed in Admin → Data';
const OPEN_GROUP_TEXT = 'Save or cancel the open group first';

/**
 * "up to 9 Auto groups re-price next, ahead of the schedule" (an upper
 * bound: the server queues only the groups whose floor moves); none says
 * so (the box is off)
 */
export function floorRepriceText(n) {
  const auto = count(n);
  if (!auto) return NO_REPRICE_TEXT;
  return `up to ${plural(auto, 'Auto group')} re-price${auto === 1 ? 's' : ''} next, ahead of the schedule`;
}

/**
 * Why "Everything" can't be picked now, or null: a saved group is open in
 * the editor (followBlocked, the follow buttons' guard), and its Save would
 * put back the own floor "Everything" clears. Null when there is no
 * Everything to pick.
 * @param {object|null} choices  floorApplyChoices
 * @param {string|boolean|null} followBlocked  the reason, or null
 */
export function applyAllBlockedReason(choices, followBlocked) {
  if (!followBlocked || count(choices?.exceptions) === 0) return null;
  return typeof followBlocked === 'string' ? followBlocked : OPEN_GROUP_TEXT;
}

/**
 * The runner state under "Update prices now" (the auto-status answer, read
 * as the Auto panel does): switched off, no working Skybox login, dry run,
 * as [{ text, warn }]. With no status yet: reading (pending), could not
 * read (failed), or nothing.
 * @param {object|null} status  the auto-status query's data
 * @param {{ pending?: boolean, failed?: boolean }} [query]
 */
export function floorRepriceNotes(status, { pending = false, failed = false } = {}) {
  if (!status || typeof status !== 'object') {
    if (pending) return [{ text: "Reading Lion Auto's state…", warn: false }];
    if (failed) return [{ text: 'Could not read whether Lion Auto is on or in dry run.', warn: true }];
    return [];
  }
  const notes = [];
  if (!isEnabled(status)) notes.push('Lion Auto is switched off: nothing re-prices until it is on');
  if (NO_LOGIN_STATUSES.includes(status.status)) notes.push(NO_LOGIN_TEXT);
  if (isDryRun(status)) notes.push('Dry run is on: runs record what they would write, no prices change');
  return notes.map(text => ({ text, warn: true }));
}

/** The PUT floors answer without what the save did (applied, reprice): the GET shape the cache holds */
export function floorsOfSave(data) {
  if (!data || typeof data !== 'object') return data;
  const { applied, reprice, ...floors } = data;
  return floors;
}

// applied { groups: [ids], shows: [eventIds] }: what "Everything" cleared
function appliedText(applied) {
  const groups = sizeOf(applied.groups);
  const shows = sizeOf(applied.shows);
  if (!groups && !shows) return 'No group or show floor needed clearing.';
  const followed = [];
  if (groups) followed.push(plural(groups, 'group'));
  if (shows) followed.push(plural(shows, 'show floor'));
  const one = followed.length === 1 && groups + shows === 1;
  return `${followed.join(' and ')} now follow${one ? 's' : ''}.`;
}

const REPRICE_FAILED_TEXT = 'But the re-price could not be queued: those groups use the new floor on their next run.';

// Why a queued run won't write yet: switched off, no working login, dry run
function repriceModeText(reprice) {
  if (reprice.enabled === false) return ' (Lion Auto is off)';
  if (NO_LOGIN_STATUSES.includes(reprice.status)) return ` (${NO_LOGIN_TEXT})`;
  if (reprice.dryRun === true) return ' (dry run: nothing is written)';
  return '';
}

// None queued means "no floor changed" only when moved says so (moved not
// sent: unknown)
function queuedText(reprice) {
  const queued = sizeOf(reprice.queued);
  const moved = sizeOf(reprice.moved);
  if (queued) {
    const groups = moved > queued ? `${queued} of ${plural(moved, 'Auto group')}` : plural(queued, 'Auto group');
    return `${groups} queued to re-price${repriceModeText(reprice)}.`;
  }
  if (reprice.moved === undefined || reprice.moved === null) return 'No Auto group was queued.';
  if (!moved) return "No Auto group's floor changed: none queued.";
  const one = moved === 1;
  return `No Auto group was queued: the ${moved} whose floor changed ${one ? 'uses' : 'use'} it on ${one ? 'its' : 'their'} next run.`;
}

// reprice { queued, moved, notAuto, enabled, dryRun, status, failed? }: what
// "Update prices now" did (failed: the floors saved, the queue did not)
function repriceTexts(reprice, scope) {
  const out = [reprice.failed === true ? REPRICE_FAILED_TEXT : queuedText(reprice)];
  const notAuto = sizeOf(reprice.notAuto);
  if (notAuto) {
    const where = scope === 'artist' ? `on ${notAuto === 1 ? 'its' : 'their'} show` : 'here';
    out.push(`${plural(notAuto, 'group')} not on Auto: Calculate → Apply ${where}.`);
  }
  return out;
}

/**
 * What a floor save did: the saved floor (floorSavedText), then what
 * "Everything" cleared (data.applied) and what "Update prices now" queued
 * (data.reprice), each only when the answer has it.
 * @param {object|null} data  the PUT floors answer
 * @param {{ scope: 'artist'|'show', pct: number, performerName?: string|null }} saved
 */
export function floorAppliedText(data, { scope, pct, performerName = null } = {}) {
  const parts = [floorSavedText(scope, pct, performerName)];
  const applied = data?.applied;
  if (applied && typeof applied === 'object') parts.push(appliedText(applied));
  const reprice = data?.reprice;
  if (reprice && typeof reprice === 'object') parts.push(...repriceTexts(reprice, scope));
  return parts.join(' ');
}

/** The result line's style: 'warn' when the floors saved but the re-price could not be queued, else 'ok' */
export function floorAppliedKind(data) {
  return data?.reprice?.failed === true ? 'warn' : 'ok';
}

/** What a follow did: { cleared: [ids], skipped: [ids] } (skipped: any group with nothing to inherit) */
export function followResultText(result) {
  const cleared = Array.isArray(result?.cleared) ? result.cleared.length : 0;
  const skipped = Array.isArray(result?.skipped) ? result.skipped.length : 0;
  if (!cleared && !skipped) return 'No group changed: they already follow.';
  const head = cleared ? `${plural(cleared, 'group')} now follow${cleared === 1 ? 's' : ''}.` : 'No group changed.';
  if (!skipped) return head;
  const one = skipped === 1;
  return `${head} ${skipped} skipped: ${one ? 'it' : 'they'} would have no floor.`;
}

const cents = (pct) => Math.round(cleanFloorPct(pct) * 100);

/**
 * A ladder answer priced at another floor than the one the group uses now
 * (answer.floor.pct; null or a missing floor = 0): our floors copy or the
 * server's moved on since Calculate, so it must not be applied. False with
 * no answer.
 * @param {object|null} answer  the ladder answer (continuations merged in)
 * @param {*} expectedPct  effectiveFloor(own, inherited).pct now
 */
export function ladderFloorChanged(answer, expectedPct) {
  if (!answer || typeof answer !== 'object') return false;
  return cents(answer.floor?.pct) !== cents(expectedPct);
}

/** The ladder bar's / Apply modal's line for a changed floor, or null */
export function ladderFloorChangedText(answer, expectedPct) {
  if (!ladderFloorChanged(answer, expectedPct)) return null;
  const word = (pct) => (cleanFloorPct(pct) > 0 ? pctText(cleanFloorPct(pct)) : 'none');
  return `The floor changed since Calculate (${word(answer.floor?.pct)} then, ${word(expectedPct)} now): Calculate again before applying.`;
}

// FLOOR_IN_USE: the Auto groups a clear would leave with no floor, named
// (those of another show of the artist say so)
function inUseText(groups, eventId) {
  const list = Array.isArray(groups) ? groups.filter(g => g && typeof g === 'object') : [];
  if (!list.length) {
    return 'Not cleared: some groups on Auto would have no floor. Give them a floor of their own, or turn Auto off, first.';
  }
  const names = list.slice(0, MAX_NAMED_GROUPS).map((g) => {
    const other = eventId != null && g.eventId != null && String(g.eventId) !== String(eventId);
    return `“${g.name || `#${g.id}`}”${other ? ' (another show)' : ''}`;
  });
  const more = list.length - names.length;
  let named;
  if (more > 0) named = `${names.join(', ')} and ${more} more`;
  else if (names.length > 1) named = `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  else named = names[0];
  const one = list.length === 1;
  return `Not cleared: ${named} ${one ? 'is' : 'are'} on Auto and would have no floor. `
    + `Give ${one ? 'it a floor of its own' : 'them a floor of their own'}, or turn Auto off, first.`;
}

// FLOOR_GROUP_REFUSED: a group "Everything" would clear could not take the
// new floor, so the whole save rolled back. The server's words name it.
function refusedText(data) {
  if (typeof data.error === 'string' && data.error.trim()) return data.error.trim();
  const g = data.group && typeof data.group === 'object' ? data.group : null;
  const name = g ? g.name || (g.id != null ? `#${g.id}` : '') : '';
  return name
    ? `Group “${name}” could not follow the new floor, so nothing was saved.`
    : 'A group could not follow the new floor, so nothing was saved.';
}

// GROUP_INVALID / CRITERIA_INVALID: the group store refused a write in the
// save or a follow, which rolled back
function groupRefusedText(data) {
  const msg = String(data.error || 'a group was not accepted').trim().replace(/\.+$/, '');
  const lines = detailLines(data.details);
  return `Nothing was saved: ${msg}${lines.length ? ` (${lines.join('; ')})` : ''}.`;
}

const NO_ANSWER = 'The server did not answer: reload to see whether it saved.';

/**
 * A failed floors call in words: the contract's codes (FLOOR_INVALID with
 * its details, ARTIST_SCOPE, ARTIST_UNKNOWN, FLOOR_IN_USE naming the
 * groups, FLOOR_GROUP_REFUSED, GROUP_INVALID / CRITERIA_INVALID from the
 * group store, ADMIN_ONLY), then the generic answers.
 * @param {object} err  the axios error
 * @param {{ fallback?: string, eventId?: string|number|null, read?: boolean }} [opts]
 *   eventId: this page's event (FLOOR_IN_USE marks other shows' groups);
 *   read: a GET (no "whether it saved" on a network failure)
 */
export function floorErrorText(err, { fallback = 'Saving the floor failed.', eventId = null, read = false } = {}) {
  const status = err?.response?.status;
  const data = err?.response?.data || {};
  if (status === 429) return `Too many requests — try again in ${retryAfterSec(err)} s.`;
  if (data.code === 'FLOOR_INVALID') {
    const lines = detailLines(data.details);
    return `The floor was not accepted${lines.length ? `: ${lines.join('; ')}` : '.'}`;
  }
  if (data.code === 'ARTIST_SCOPE') {
    return "Only an admin, or a pricer assigned to this artist, can change the artist floor. You can still set this show's floor.";
  }
  if (data.code === 'ARTIST_UNKNOWN') {
    return "This show's performer is not known, so it has no artist floor. Set this show's floor instead.";
  }
  if (data.code === 'FLOOR_IN_USE') return inUseText(data.groups, eventId);
  if (data.code === 'FLOOR_GROUP_REFUSED') return refusedText(data);
  if (data.code === 'GROUP_INVALID' || data.code === 'CRITERIA_INVALID') return groupRefusedText(data);
  // "Update prices now" is admin-like only; the server refuses before any write
  if (data.code === 'ADMIN_ONLY') return 'Only an admin can re-price now. The floor was not saved.';
  if (status === 403) return 'You do not have access to this event.';
  // A bare { error } with no code is the server's unknown-route answer
  if (status === 404) return data.code ? (data.error || 'Not found.') : 'Floors are not available on this server yet.';
  if (status === 400) return data.error || 'The floor was not accepted.';
  if (!err) return fallback;
  if (!status) return read ? fallback : NO_ANSWER;
  return data.error || fallback;
}
